import { AutoCompleteProps } from 'antd';
import useGenericSearch from 'hooks/useGenericSearch';
import { useState } from 'react';

export interface PaginatedSearchArgs<T> {
  dataKey: keyof T;
  url: string;
  mapDataToOptions: (data: T[]) => { value: string; key: string }[];
  onSearch?: (value: string) => void;
  onSelect?: (
    option: { value?: string | number | null | undefined; key?: string },
    selected?: T,
  ) => void;
}

export function usePaginatedSearch<T>({
  dataKey,
  url,
  mapDataToOptions,
  onSearch,
  onSelect,
}: PaginatedSearchArgs<T>) {
  const [search, setSearch] = useState('');
  const {
    data = [],
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useGenericSearch<T[]>({
    search,
    url,
  });

  const handleSearch = (value: string) => {
    onSearch?.(value);

    setTimeout(() => {
      setSearch(value);
    }, 10);
  };

  const handlePopupScroll = (e: React.UIEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight &&
      hasNextPage &&
      !isLoading
    ) {
      fetchNextPage();
    }
  };

  const handleSelect: AutoCompleteProps['onSelect'] = (_, option) => {
    const selectedItem = data.find((item: T) => item[dataKey] === option.key);

    onSelect?.(option, selectedItem);
  };

  return {
    options: mapDataToOptions(data),
    onSearch: handleSearch,
    onPopupScroll: handlePopupScroll,
    onSelect: handleSelect,
    isLoading,
  };
}
